/**
 * Procedural maze generator using the Recursive Backtracker algorithm.
 *
 * Produces a 2D grid where each cell is either WALL (1) or PATH (0).
 * The grid uses an "expanded" layout: the actual maze cells sit on
 * odd-indexed rows/columns, with walls on even-indexed rows/columns.
 *
 * Grid dimensions: (2 * cols + 1) x (2 * rows + 1)
 *
 * @example
 *   const gen = new MazeGenerator(15, 13);
 *   const { grid, start, exit } = gen.generate();
 */

import { CellType } from '../utils/constants.js';
import {
  BASE_MAZE_COLS,
  BASE_MAZE_ROWS,
  MAZE_GROWTH_COLS,
  MAZE_GROWTH_ROWS,
  MAX_MAZE_COLS,
  MAX_MAZE_ROWS,
} from '../config/game-config.js';

export default class MazeGenerator {
  /**
   * @param {number} cols - Number of logical maze columns (cells, not grid units).
   * @param {number} rows - Number of logical maze rows.
   * @param {number} [seed] - Optional seed for reproducible mazes.
   */
  constructor(cols, rows, seed = null) {
    this.cols = cols;
    this.rows = rows;
    this.seed = seed;

    // Expanded grid dimensions
    this.gridWidth = 2 * cols + 1;
    this.gridHeight = 2 * rows + 1;

    /** @type {number[][]} */
    this.grid = [];
  }

  // ---------------------------------------------------------------
  // Static helpers
  // ---------------------------------------------------------------

  /**
   * Calculate maze dimensions for a given level.
   *
   * @param {number} level - Current game level (1-based).
   * @returns {{ cols: number, rows: number }}
   */
  static dimensionsForLevel(level) {
    const cols = Math.min(
      BASE_MAZE_COLS + (level - 1) * MAZE_GROWTH_COLS,
      MAX_MAZE_COLS,
    );
    const rows = Math.min(
      BASE_MAZE_ROWS + (level - 1) * MAZE_GROWTH_ROWS,
      MAX_MAZE_ROWS,
    );
    // Ensure odd numbers for clean maze generation
    return {
      cols: cols % 2 === 0 ? cols + 1 : cols,
      rows: rows % 2 === 0 ? rows + 1 : rows,
    };
  }

  // ---------------------------------------------------------------
  // Seeded random (simple LCG)
  // ---------------------------------------------------------------

  /** @private */
  _initRandom() {
    if (this.seed !== null) {
      this._rngState = this.seed;
    } else {
      this._rngState = Date.now();
      this.seed = this._rngState;
    }
  }

  /**
   * Returns a pseudo-random float in [0, 1).
   * @private
   */
  _random() {
    // Linear Congruential Generator
    this._rngState = (this._rngState * 1664525 + 1013904223) & 0xffffffff;
    return (this._rngState >>> 0) / 0x100000000;
  }

  /**
   * Shuffle an array in place using Fisher-Yates.
   * @private
   */
  _shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this._random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // ---------------------------------------------------------------
  // Generation
  // ---------------------------------------------------------------

  /**
   * Generate the maze.
   *
   * @returns {{
   *   grid: number[][],
   *   start: { col: number, row: number },
   *   exit: { col: number, row: number },
   *   gridWidth: number,
   *   gridHeight: number,
   *   seed: number,
   *   rooms: Array<{ type: string, cx: number, cy: number, size: number, cells: Array<{ col: number, row: number }> }>
   * }}
   */
  generate() {
    this._initRandom();
    this._initGrid();
    this._carve(0, 0);

    const start = this._placeStart();
    const exit = this._placeExit(start);

    return {
      grid: this.grid,
      start,
      exit,
      gridWidth: this.gridWidth,
      gridHeight: this.gridHeight,
      seed: this.seed,
      rooms: [],
    };
  }

  /** @private Fill the grid entirely with walls. */
  _initGrid() {
    this.grid = [];
    for (let y = 0; y < this.gridHeight; y++) {
      const row = [];
      for (let x = 0; x < this.gridWidth; x++) {
        row.push(CellType.WALL);
      }
      this.grid.push(row);
    }
  }

  /**
   * Recursive backtracker: carve paths starting from logical cell (cx, cy).
   * @private
   */
  _carve(cx, cy) {
    // Mark this cell as path in expanded grid
    const gx = 2 * cx + 1;
    const gy = 2 * cy + 1;
    this.grid[gy][gx] = CellType.PATH;

    // Visit neighbors in random order
    const directions = this._shuffle([
      { dx: 0, dy: -1 }, // up
      { dx: 0, dy: 1 },  // down
      { dx: -1, dy: 0 }, // left
      { dx: 1, dy: 0 },  // right
    ]);

    for (const { dx, dy } of directions) {
      const nx = cx + dx;
      const ny = cy + dy;

      // Check bounds
      if (nx < 0 || nx >= this.cols || ny < 0 || ny >= this.rows) {
        continue;
      }

      const ngx = 2 * nx + 1;
      const ngy = 2 * ny + 1;

      // Only visit unvisited cells
      if (this.grid[ngy][ngx] === CellType.WALL) {
        // Remove wall between current and neighbor
        const wallX = gx + dx;
        const wallY = gy + dy;
        this.grid[wallY][wallX] = CellType.PATH;

        this._carve(nx, ny);
      }
    }
  }

  /**
   * Pick a random walkable cell for the player start position.
   * Uses the seeded RNG so the position is reproducible from the same seed.
   * @private
   * @returns {{ col: number, row: number }} Grid coordinates.
   */
  _placeStart() {
    const pathCells = MazeGenerator.getPathCells(this.grid);
    const idx = Math.floor(this._random() * pathCells.length);
    return pathCells[idx];
  }

  /**
   * Pick a random walkable cell for the maze exit, ensuring a minimum
   * Manhattan distance from the start so the level isn't trivially short.
   * The minimum distance scales with maze size (at least 40% of the sum
   * of grid dimensions).
   * @private
   * @param {{ col: number, row: number }} start
   * @returns {{ col: number, row: number }} Grid coordinates.
   */
  _placeExit(start) {
    const pathCells = MazeGenerator.getPathCells(this.grid);
    const minDist = Math.floor((this.gridWidth + this.gridHeight) * 0.4);

    // Filter cells that are far enough from start
    const candidates = pathCells.filter((cell) => {
      const dist = Math.abs(cell.col - start.col) + Math.abs(cell.row - start.row);
      return dist >= minDist;
    });

    // Pick a random candidate; fallback to the farthest cell if none qualify
    if (candidates.length > 0) {
      const idx = Math.floor(this._random() * candidates.length);
      return candidates[idx];
    }

    // Fallback: pick the farthest cell from start
    let best = pathCells[0];
    let bestDist = 0;
    for (const cell of pathCells) {
      const d = Math.abs(cell.col - start.col) + Math.abs(cell.row - start.row);
      if (d > bestDist) {
        bestDist = d;
        best = cell;
      }
    }
    return best;
  }

  // ---------------------------------------------------------------
  // Utility queries
  // ---------------------------------------------------------------

  /**
   * Check if a grid cell is walkable (PATH).
   *
   * @param {number[][]} grid
   * @param {number} col - Grid column.
   * @param {number} row - Grid row.
   * @returns {boolean}
   */
  static isWalkable(grid, col, row) {
    if (row < 0 || row >= grid.length || col < 0 || col >= grid[0].length) {
      return false;
    }
    return grid[row][col] === CellType.PATH;
  }

  /**
   * Get all walkable (PATH) cell positions in the grid.
   *
   * @param {number[][]} grid
   * @returns {{ col: number, row: number }[]}
   */
  static getPathCells(grid) {
    const cells = [];
    for (let row = 0; row < grid.length; row++) {
      for (let col = 0; col < grid[0].length; col++) {
        if (grid[row][col] === CellType.PATH) {
          cells.push({ col, row });
        }
      }
    }
    return cells;
  }
}
